import styled from 'styled-components';
import { Col } from 'antd';
import { StyledAntCard } from 'components/styledComponents/card/Card.styled';
import {
  truncate, getBoxShadow, flexContainer, media,
} from 'utils/styledHelpers';
import { paymentColors } from 'constants/mapping';
import { PaymentRequestStatuses } from 'constants/enumTypes1';
import { CSSProperties } from 'react';

interface StyledOverviewGridItemProps {
  $gridArea?: CSSProperties['gridArea'];
}
interface StyledOverviewItemHeaderProps {
  $oneChild?: boolean;
}
interface StyledTotalPaymentTitleProps {
  $isAmount?: boolean;
}
interface StyledOverviewRevenueItemValueProps {
  $isNegative?: boolean;
}
interface StyledRevenuePopoverValueProps {
  $isNegative?: boolean;
}
interface StyledTagExtraDetailsProps {
  $xxl?: boolean;
}
interface StyledCircleProps {
  $xxl?: boolean;
}
interface StyledEmptyDataProps {
  $fullHeight?: boolean;
}

const StyledOverviewLimits = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0.85rem 1.71rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  svg {
    margin-right: 1.14rem;
  }
`;
const StyledNoGamingLimits = styled.div`
  min-height: 3.2rem;
`;

const StyledOverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(4, 1fr);
  grid-column-gap: 10px;
  grid-row-gap: 10px;

  @media only screen and (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr repeat(2, 2fr) 1fr;
  }

  @media only screen and (min-width: 768px) and (max-width: 1200px) {
    grid-template-columns: 100%;
    grid-template-rows: repeat(2, 1fr) repeat(4, 2fr) repeat(2, 1fr);
  }

  ${media.xs`
    grid-template-columns: 100%;
    grid-template-rows: repeat(2, 1fr) repeat(4, 2fr) repeat(2, 1fr);
  `}
`;
const StyledOverviewGridItem = styled.div<StyledOverviewGridItemProps>`
  grid-area: ${(props) => props.$gridArea};
  min-width: 0;
`;

const StyledTotalPaymentsOverviewItem = styled.div`
  height: 100%;
  > .ant-card {
    padding: 1.71rem;
  }
`;
const StyledRevenueOverviewItem = styled.div`
  height: 100%;
  > .ant-card {
    background-color: ${(props) => props.theme.colors.blue}10;
    padding-right: 1.71rem !important;
  }
`;

const StyledActivitiesOverviewItem = styled.div`
  height: 100%;
  > .ant-card {
    padding-right: 1.71rem;
    padding-top: 0.7rem;
  }
  .ant-card-head-title {
    overflow: visible;
  }
`;
const StyledAntCardOverview = styled(StyledAntCard)`
  &.ant-card {
    padding: 1rem 1.14rem 1rem 1.71rem;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    &:hover {
      box-shadow: ${(props) => getBoxShadow(props.theme.colors.secondaryShadow)};
    }
    .ant-card-body {
      position: initial;
    }
  }
`;
const StyledTotalPaymentHeading = styled.div`
  display: flex;
  align-items: center;
  column-gap: 1.28rem;
`;
const StyledTotalPaymentTitle = styled.div<StyledTotalPaymentTitleProps>`
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-size: ${(props) => (props.$isAmount ? '1.71rem' : props.theme.typography.fontSizeMiddle)};
  ${truncate()};
`;
const StyledTotalPaymentList = styled.ul`
  margin-top: 1.71rem;

  li {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 0.86rem;
    }

    strong {
      font-weight: ${(props) => props.theme.typography.fontWeightMedium};
      font-size: ${(props) => props.theme.typography.fontSizeMiddle};
    }
  }
`;

const StyledOverviewItemHeader = styled.div<StyledOverviewItemHeaderProps>`
  display: flex;
  align-items: flex-start;
  justify-content: ${(props) => (props.$oneChild ? 'flex-end' : 'space-between')};
  column-gap: 8px;
  h2 {
    font-size: ${(props) => props.theme.typography.fontSizeMiddle};
    ${truncate()};
  }
  & > time {
    font-size: ${(props) => props.theme.typography.fontSizeSmall};
    font-weight: ${(props) => props.theme.typography.fontWeightRegular};
    color: ${(props) => props.theme.colors.filterLabelColor};
    white-space: nowrap;
  }
`;

const StyledOverviewItemHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  column-gap: 9px;
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-size: 1.27rem;
  margin-bottom: 1.71rem;
  ${truncate()};
`;
const StyledOverviewItemStatus = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  column-gap: 9px;
`;
const StyledOverviewItemStatusName = styled.div<{$paymentStatus: PaymentRequestStatuses}>`
  color: ${(props) => paymentColors[props.$paymentStatus]};
  font-size: ${(props) => props.theme.typography.fontSizeSmall};

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 100%;
    left: 0;
    top: 0;
    background-color: ${(props) => paymentColors[props.$paymentStatus]};
  }
`;

const StyledOverviewPaymentIconWrapper = styled.div`
  display: flex;
  margin: 6px 0 10px;
  img,
  svg {
    width: 116px;
    height: 52px;
    object-fit: contain;
    object-position: left;
  }
`;
const StyledOverviewPaymentInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 0.57rem;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }

  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 8px;

    span {
      line-height: 0.95rem;
      font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    }
  }
  & > span {
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    line-height: 0.95rem;
    ${truncate()};
  }
`;

const StyledOverviewRevenue = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 1rem;

  &:not(:last-of-type) {
    background-image: linear-gradient(to right, ${(props) => props.theme.colors.primaryBorder} 55%, rgba(255, 255, 255, 0) 0%);
    background-position: bottom;
    background-size: 12px 1px;
    background-repeat: repeat-x;
  }

  &:last-child {
    padding: 1rem 0 0;
  }
`;

const StyledOverviewRevenueInfoItem = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 0.57rem;
  p {
    font-size: ${(props) => props.theme.typography.fontSizeMiddle};
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  }
`;

const StyledOverviewRevenueItemValue = styled.span<StyledOverviewRevenueItemValueProps>`
  font-size: ${(props) => props.theme.typography.fontSizeMiddle};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => (props.$isNegative ? props.theme.colors.danger : props.theme.colors.blue)};
  ${truncate()};
`;
const StyledRevenueDetailsSvg = styled.div`
  cursor: pointer;
  padding: 0.2rem 0.2rem 0.2rem 0.65rem;
  svg {
    transition: 0.3s transform;
  }
  &.ant-popover-open svg {
    transform: rotate(90deg);
  }
`;
const StyledRevenuePopoverItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 13rem;
  padding: 0.6rem 1.4rem;
  p {
    line-height: 1rem;
  }
`;
const StyledRevenuePopoverValue = styled.p<StyledRevenuePopoverValueProps>`
  color: ${(props) => (props.$isNegative ? props.theme.colors.danger : props.theme.colors.blue)};
`;

const StyledActivitiesCol = styled(Col)`
  margin-top: 1.14rem;
  max-height: 29rem;
`;
const StyledActivitiesCard = styled(StyledAntCardOverview)`
  div.ant-card-head {
    padding-left: 0;
    background-color: transparent;
    min-height: auto;
    border: none;
    border-radius: 0;

    .ant-card-head-title {
      padding: 0;
    }
  }
`;
const StyledActivitiesBlockHeading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 9px;
  margin: 1.14rem 0;
  padding: 0.57rem 0;
  background-color: ${(props) => props.theme.colors.tableTrEvenColor};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};

  span {
    line-height: 1.14rem;
  }
`;
const StyledCardHeadWithExtra = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${(props) => props.theme.typography.fontSizeMiddle};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  padding-bottom: 0.7rem;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: -1.71rem;
    width: calc(100% + 3.42rem);
    height: 1px;
    background-color: ${(props) => props.theme.colors.secondaryBorder};
  }
`;
const StyledActivitiesViewAll = styled.span`
  font-size: ${(props) => props.theme.typography.fontSizeSmall};
  color: ${(props) => props.theme.colors.primaryText};
  cursor: pointer;
  text-decoration: underline;
`;
const StyledActivitiesItemsList = styled.ul`
  &:not(:last-of-type) {
    padding-bottom: 1.14rem;
    background-image: linear-gradient(to right, ${(props) => props.theme.colors.primaryBorder} 55%, rgba(255, 255, 255, 0) 0%);
    background-position: bottom;
    background-size: 20px 1px;
    background-repeat: repeat-x;
  }
`;
const StyledActivitiesItemsListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 0.57rem;

  &:not(:last-child) {
    margin-bottom: 0.57rem;
  }
`;
const StyledActivitiesFlagWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const StyledActivitiesFlag = styled.div`
  margin-left: 0.57rem;
`;

const StyledTagsOverviewItem = styled(StyledActivitiesOverviewItem)`
  .ant-card {
    padding-left: 0 !important;
    padding-right: 0 !important;
    .ant-card-head {
      padding-left: 1.71rem;
      padding-right: 1.71rem;
    }
    .ant-card-body {
      flex: 100%;
      padding: 0 1.71rem;
      overflow: auto;
      margin-top: 1.14rem;
    }
  }
`;
const StyledTagInfoWrapper = styled.div`
  background-color: ${(props) => props.theme.colors.primaryBg};
  padding: 1.14rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};

  &:not(:last-child) {
    margin-bottom: 1.71rem;
  }
`;
const StyledTagInfoRow = styled.div`
  ${flexContainer()}
  margin-bottom: .57rem;
  &:first-of-type {
    margin-bottom: 0.86rem;
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;
const StyledTagExtraDetails = styled.div<StyledTagExtraDetailsProps>`
  display: flex;
  flex-direction: ${(props) => (props.$xxl ? 'row' : 'column')};
  align-items: ${(props) => (props.$xxl ? 'center' : 'flex-end')};
  justify-content: flex-end;
  color: ${(props) => props.theme.colors.filterLabelColor};
  ${truncate()}

  & > * {
    max-width: 100%;
    font-size: ${(props) => props.theme.typography.fontSizeSmall};
  }
`;
const StyledCircle = styled.span<StyledCircleProps>`
  display: ${(props) => (props.$xxl ? 'block' : 'none')};
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.filterLabelColor};
  margin: 0 0.7rem;
`;
const StyledTagDescription = styled.p`
  border-left: 2px solid ${(props) => props.theme.colors.primaryBorder};
  padding-left: 0.87rem;
  margin-top: 1.14rem;
`;
const StyledEmptyData = styled.div<StyledEmptyDataProps>`
  background-color: ${(props) => props.theme.colors.tableTrEvenColor};
  height: ${(props) => (props.$fullHeight ? 'calc(100% - 1.14rem)' : 'auto')};
  margin: 0.57rem 0;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  .ant-empty-normal {
    ${flexContainer({ align: 'center', justify: 'center', direction: 'column' })}
    height: ${(props) => (props.$fullHeight ? '100%' : 'auto')};
    padding: 1.7rem 0;
    margin: 0;
  }
`;

export {
  StyledOverviewLimits,
  StyledNoGamingLimits,
  StyledOverviewGrid,
  StyledOverviewGridItem,
  StyledTotalPaymentsOverviewItem,
  StyledRevenueOverviewItem,
  StyledActivitiesOverviewItem,
  StyledAntCardOverview,
  StyledTotalPaymentHeading,
  StyledTotalPaymentTitle,
  StyledTotalPaymentList,
  StyledOverviewItemHeader,
  StyledOverviewItemHeaderLeft,
  StyledOverviewItemStatus,
  StyledOverviewItemStatusName,
  StyledOverviewPaymentIconWrapper,
  StyledOverviewPaymentInfoWrapper,
  StyledOverviewRevenue,
  StyledOverviewRevenueInfoItem,
  StyledOverviewRevenueItemValue,
  StyledRevenueDetailsSvg,
  StyledRevenuePopoverItem,
  StyledRevenuePopoverValue,
  StyledActivitiesCol,
  StyledActivitiesCard,
  StyledActivitiesBlockHeading,
  StyledCardHeadWithExtra,
  StyledActivitiesViewAll,
  StyledActivitiesItemsList,
  StyledActivitiesItemsListItem,
  StyledActivitiesFlagWrapper,
  StyledActivitiesFlag,
  StyledTagsOverviewItem,
  StyledTagInfoWrapper,
  StyledTagInfoRow,
  StyledTagExtraDetails,
  StyledCircle,
  StyledTagDescription,
  StyledEmptyData,
};
